import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { ArrowUpRight, ArrowDownRight, RefreshCw, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import analyticsService from '../services/analyticsService';

const TradeHistory = ({ userId = 'demo_user', limit = 20 }) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const data = await analyticsService.getTradeHistory(userId, limit);
      setTrades(data);
    } catch (error) {
      toast.error('Failed to load trade history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades();
  }, [userId]);

  const formatAmount = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0';
    return num.toFixed(4);
  };

  const getTradeIcon = (type) => {
    if (type === 'swap') return <TrendingUp className="h-5 w-5 text-blue-600" />;
    if (type === 'send') return <ArrowUpRight className="h-5 w-5 text-red-600" />;
    return <ArrowDownRight className="h-5 w-5 text-green-600" />;
  };

  return (
    <Card data-testid="trade-history-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Trade History</CardTitle>
            <CardDescription>Your recent {limit} trades</CardDescription>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchTrades}
            disabled={loading}
            data-testid="refresh-trades-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && trades.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">Loading trades...</p>
          </div>
        ) : trades.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">No trades yet</p>
            <p className="text-xs text-gray-400 mt-2">Start trading to see your history</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-3">
              {trades.map((trade, index) => (
                <div
                  key={index}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`trade-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1">{getTradeIcon(trade.type)}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium capitalize">{trade.type}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(trade.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">
                              {formatAmount(trade.amount_in)} {trade.token_in}
                            </p>
                            <p className="text-xs text-gray-500">
                              → {formatAmount(trade.amount_out)} {trade.token_out}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">TX: {trade.tx_hash?.substring(0, 10)}...</span>
                          <span className={
                            trade.status === 'completed' 
                              ? 'text-green-600 font-medium' 
                              : 'text-yellow-600 font-medium'
                          }>
                            {trade.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default TradeHistory;
